import React, { useState, useEffect } from "react";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";

const DownloadModal = ({ update, onClose, onComplete }) => {
  const [phase, setPhase] = useState("confirm"); // confirm | downloading | complete | renewal
  const [progress, setProgress] = useState(0);

  const isExpired = update?.status === "expirado";

  useEffect(() => {
    if (phase === "downloading") {
      const interval = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 100) {
            clearInterval(interval);
            setPhase("complete");
            onComplete(update);
            return 100;
          }
          return prev + Math.floor(Math.random() * 15) + 5;
        });
      }, 400);
      return () => clearInterval(interval);
    }
  }, [phase]);

  if (!update) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
      <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-xl" style={{ boxShadow: "var(--shadow-xl)" }}>
        {phase === "confirm" && (
          <>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: isExpired ? "var(--color-warning)" : "var(--color-primary)", opacity: 0.9 }}>
                <Icon name={isExpired ? "RefreshCw" : "Download"} size={22} color="white" />
              </div>
              <div>
                <h3 className="font-bold text-base" style={{ fontFamily: "var(--font-heading)", color: "var(--color-foreground)" }}>
                  {isExpired ? "Solicitar renovación" : "Confirmar descarga"}
                </h3>
                <p className="text-xs" style={{ color: "var(--color-muted-foreground)" }}>{update?.name} · v{update?.version}</p>
              </div>
            </div>
            {isExpired ? (
              <p className="text-sm mb-5" style={{ color: "var(--color-muted-foreground)" }}>
                El enlace de descarga para esta actualización ha expirado. Se enviará una solicitud de renovación al administrador.
              </p>
            ) : (
              <div className="space-y-2 mb-5">
                <div className="flex justify-between text-sm">
                  <span style={{ color: "var(--color-muted-foreground)" }}>Tamaño del archivo</span>
                  <span className="font-medium" style={{ color: "var(--color-foreground)" }}>{update?.fileSize}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span style={{ color: "var(--color-muted-foreground)" }}>Versión</span>
                  <span className="font-medium font-mono" style={{ color: "var(--color-foreground)" }}>{update?.version}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span style={{ color: "var(--color-muted-foreground)" }}>URL firmada válida hasta</span>
                  <span className="font-medium" style={{ color: "var(--color-foreground)" }}>{update?.expirationDate}</span>
                </div>
              </div>
            )}
            <div className="flex gap-3">
              <Button variant="default" fullWidth onClick={() => isExpired ? setPhase("renewal") : setPhase("downloading")}>
                {isExpired ? "Enviar solicitud" : "Iniciar descarga"}
              </Button>
              <Button variant="outline" onClick={onClose}>Cancelar</Button>
            </div>
          </>
        )}

        {phase === "downloading" && (
          <div className="text-center py-4">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: "var(--color-primary)", opacity: 0.9 }}>
              <Icon name="Download" size={28} color="white" />
            </div>
            <h3 className="font-bold text-base mb-1" style={{ fontFamily: "var(--font-heading)", color: "var(--color-foreground)" }}>
              Descargando...
            </h3>
            <p className="text-xs mb-4" style={{ color: "var(--color-muted-foreground)" }}>{update?.name} · v{update?.version}</p>
            <div className="w-full rounded-full h-2 mb-2" style={{ backgroundColor: "var(--color-muted)" }}>
              <div
                className="h-2 rounded-full transition-all duration-300"
                style={{ width: `${Math.min(progress, 100)}%`, backgroundColor: "var(--color-primary)" }}
              />
            </div>
            <p className="text-sm font-semibold" style={{ color: "var(--color-primary)" }}>{Math.min(progress, 100)}%</p>
          </div>
        )}

        {phase === "complete" && (
          <div className="text-center py-4">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: "var(--color-success)" }}>
              <Icon name="CheckCircle" size={28} color="white" />
            </div>
            <h3 className="font-bold text-base mb-1" style={{ fontFamily: "var(--font-heading)", color: "var(--color-foreground)" }}>
              ¡Descarga completada!
            </h3>
            <p className="text-sm mb-5" style={{ color: "var(--color-muted-foreground)" }}>
              {update?.name} v{update?.version} se descargó correctamente.
            </p>
            <Button variant="default" onClick={onClose}>Cerrar</Button>
          </div>
        )}

        {phase === "renewal" && (
          <div className="text-center py-4">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: "var(--color-warning)" }}>
              <Icon name="Send" size={28} color="white" />
            </div>
            <h3 className="font-bold text-base mb-1" style={{ fontFamily: "var(--font-heading)", color: "var(--color-foreground)" }}>
              Solicitud enviada
            </h3>
            <p className="text-sm mb-5" style={{ color: "var(--color-muted-foreground)" }}>
              Su solicitud de renovación fue enviada al administrador. Recibirá una notificación cuando sea aprobada.
            </p>
            <Button variant="default" onClick={onClose}>Cerrar</Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DownloadModal;